import React from "react";
import Table from "../../../core/common/dataTable/index";
import * as XLSX from "xlsx";

interface ODReportDetailsProps {
  data: any[];
  onBack: () => void;
}

const ODReportDetails: React.FC<ODReportDetailsProps> = ({ data, onBack }) => {
  // Get unique statuses from the data to decide which columns to show
  const statuses = Array.from(
    new Set(data.map((item) => item.status?.toUpperCase()))
  );

  const columns: any[] = [
    { title: "EmpCode", dataIndex: "employee_id" },
    { title: "EmployeeName", dataIndex: "employee_name" },
    { title: "Email", dataIndex: "email_id" },
    // { title: "LeaveType", dataIndex: "leave_type" },
    { title: "FromDate", dataIndex: "from_date" },
    { title: "ToDate", dataIndex: "to_date" },
    { title: "ODSlice", dataIndex: "leave_slice_type" },
    { title: "ApplicationDate", dataIndex: "application_date" },
    { title: "Reason", dataIndex: "application_reason" },
    { title: "ManagerEmail", dataIndex: "manager_email_id" },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => {
        let color = "";
        switch (text?.toLowerCase()) {
          case "pending":
            color = "warning";
            break;
          case "approved":
            color = "success";
            break;
          case "rejected":
            color = "danger";
            break;
          default:
            color = "secondary";
        }
        return (
          <span
            className={`badge bg-${color}`}
            style={{ textTransform: "capitalize" }}
          >
            {text || "-"}
          </span>
        );
      },
    },
  ];

  // Conditionally push approval columns if status is APPROVED
  if (statuses.includes("APPROVED")) {
    columns.push(
      {
        title: "ApprovedBy",
        render: (_: any, record: any) =>
          record.approved_by_name || record.approved_by || "-",
      },
      {
        title: "ApprovedOn",
        dataIndex: "approved_on",
        render: (value: string) => (value ? value.split("T")[0] : "-"),
      }
    );
  }

  // Conditionally push rejection columns if status is REJECTED
  if (statuses.includes("REJECTED")) {
    columns.push(
      {
        title: "RejectedBy",
        render: (_: any, record: any) =>
          record.rejected_by_name || record.rejected_by || "-",
      },
      {
        title: "RejectedOn",
        dataIndex: "rejected_on",
        render: (value: string) => (value ? value.split("T")[0] : "-"),
      },
      {
        title: "RejectionReason",
        dataIndex: "rejection_reason",
      }
    );
  }

  const exportToExcel = () => {
    const formattedData = data.map((item) => {
      const row: any = {
        EmpCode: item.employee_id,
        EmployeeName: item.employee_name,
        Email: item.email_id,
        // LeaveType: item.leave_type,
        FromDate: item.from_date,
        ToDate: item.to_date,
        ODSlice: item.leave_slice_type,
        ApplicationDate: item.application_date,
        Reason: item.application_reason,
        ManagerEmail: item.manager_email_id,
        Status: item.status,
      };

      if (item.status?.toUpperCase() === "APPROVED") {
        row["ApprovedBy"] = item.approved_by_name || item.approved_by || "-";
        row["ApprovedOn"] = item.approved_on
          ? item.approved_on.split("T")[0]
          : "-";
      }

      if (item.status?.toUpperCase() === "REJECTED") {
        row["RejectedBy"] = item.rejected_by_name || item.rejected_by || "-";
        row["RejectedOn"] = item.rejected_on
          ? item.rejected_on.split("T")[0]
          : "-";
        row["RejectionReason"] = item.rejection_reason || "-";
      }

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "OD Report");
    XLSX.writeFile(workbook, "OD_Report.xlsx");
  };

  return (
    <div className="card mt-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">OD Report Details</h5>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-primary" onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={exportToExcel}
          >
            Download Excel
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        <Table dataSource={data} columns={columns} Selection={false}  showSearchInput={true}/>
      </div>
    </div>
  );
};

export default ODReportDetails;
